import apiClient from './client';
import {
    Campaign,
    CampaignCreate,
    CampaignUpdate,
    CampaignStats,
    CampaignStatus,
    PaginatedResponse,
    FileUploadResponse,
    CampaignExecuteResponse,
    Contact,
    ContactPayload,
    ContactUpdatePayload,
    Message,
    MessageStatus,
} from '../types/campaign.types';

// Campaign API functions
export const campaignApi = {
    // Create a new campaign
    create: async (data: CampaignCreate): Promise<Campaign> => {
        const response = await apiClient.post<Campaign>('/api/campaigns/', data);
        return response.data;
    },

    // List all campaigns with pagination and filters
    list: async (params: {
        page?: number;
        page_size?: number;
        status_filter?: CampaignStatus;
        search?: string;
    }): Promise<PaginatedResponse<Campaign>> => {
        const response = await apiClient.get<PaginatedResponse<Campaign>>(
            '/api/campaigns/',
            { params }
        );
        return response.data;
    },

    // Get single campaign details
    get: async (
        campaignId: number,
        includeContacts: boolean = false
    ): Promise<Campaign> => {
        const response = await apiClient.get<Campaign>(
            `/api/campaigns/${campaignId}`,
            { params: { include_contacts: includeContacts } }
        );
        return response.data;
    },

    // Update campaign
    update: async (
        campaignId: number,
        data: CampaignUpdate
    ): Promise<Campaign> => {
        const response = await apiClient.patch<Campaign>(
            `/api/campaigns/${campaignId}`,
            data
        );
        return response.data;
    },

    // Delete campaign
    delete: async (campaignId: number): Promise<void> => {
        await apiClient.delete(`/api/campaigns/${campaignId}`);
    },

    // Get campaign statistics
    getStats: async (): Promise<CampaignStats> => {
        const response = await apiClient.get<CampaignStats>(
            '/api/campaigns/stats/overview'
        );
        return response.data;
    },

    // Upload contacts file
    uploadContacts: async (
        campaignId: number,
        file: File
    ): Promise<FileUploadResponse> => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await apiClient.post<FileUploadResponse>(
            `/api/campaigns/${campaignId}/upload`,
            formData,
            {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            }
        );
        return response.data;
    },

    // Add contacts manually
    addContacts: async (
        campaignId: number,
        contacts: Array<{
            name: string;
            phone_number: string;
            email?: string;
            custom_fields?: Record<string, any>;
        }>
    ): Promise<Contact[]> => {
        const response = await apiClient.post<Contact[]>(
            `/api/campaigns/${campaignId}/contacts`,
            { contacts }
        );
        return response.data;
    },

    // Execute campaign (send SMS)
    execute: async (
        campaignId: number,
        forceResend: boolean = false
    ): Promise<CampaignExecuteResponse> => {
        const response = await apiClient.post<CampaignExecuteResponse>(
            `/api/campaigns/${campaignId}/execute`,
            null,
            { params: { force_resend: forceResend } }
        );
        return response.data;
    },

    stop: async (campaignId: number): Promise<Campaign> => {
        const response = await apiClient.post<Campaign>(`/api/campaigns/${campaignId}/stop`);
        return response.data;
    },

    // Retry failed messages
    retryFailed: async (campaignId: number): Promise<{ task_id: string }> => {
        const response = await apiClient.post<{ task_id: string }>(
            `/api/campaigns/${campaignId}/retry`
        );
        return response.data;
    },

    // Get campaign contacts with pagination and filtering
    getContacts: async (params: {
        campaignId: number;
        page?: number;
        page_size?: number;
        validation_filter?: 'valid' | 'invalid' | 'all';
        search?: string;
    }): Promise<PaginatedResponse<Contact>> => {
        const { campaignId, ...queryParams } = params;
        const response = await apiClient.get<PaginatedResponse<Contact>>(
            `/api/campaigns/${campaignId}/contacts`,
            { params: queryParams }
        );
        return response.data;
    },

    // Add a single contact to a campaign
    addSingleContact: async (
        campaignId: number,
        contact: ContactPayload
    ): Promise<Contact> => {
        const response = await apiClient.post<Contact>(
            `/api/campaigns/${campaignId}/contacts/single`,
            contact
        );
        return response.data;
    },

    updateContact: async (
        campaignId: number,
        contactId: number,
        contact: ContactUpdatePayload
    ): Promise<Contact> => {
        const response = await apiClient.patch<Contact>(
            `/api/campaigns/${campaignId}/contacts/${contactId}`,
            contact
        );
        return response.data;
    },

    // Get campaign messages
    getMessages: async (params: {
        campaignId: number;
        page?: number;
        page_size?: number;
        status_filter?: MessageStatus;
    }): Promise<PaginatedResponse<Message>> => {
        const { campaignId, ...queryParams } = params;
        const response = await apiClient.get<PaginatedResponse<Message>>(
            `/api/campaigns/${campaignId}/messages`,
            { params: queryParams }
        );
        return response.data;
    },
};

export default campaignApi;
